import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from '@lexical/markdown';
import { $getRoot, $createParagraphNode } from 'lexical';
import { useEffect, useRef } from 'react';

import type { TextareaFormattingType } from './textarea-markdown-util';
import { TextareaMarkdownUtil } from './textarea-markdown-util';
import { TextareaReplacementUtil } from './textarea-replacement-util';

export type RawTextareaValuePluginProps = {
  formatting: TextareaFormattingType;
  /**
   * Applies the same normalisation the value receives before it is persisted, so
   * the plugin compares like with like. Without it the emitted value and the
   * incoming `value` prop would never match and the editor would re-import on
   * every keystroke.
   */
  formatValue: (markdown: string) => string | null;
  onChange?: (value: string | null) => Promise<unknown> | void;
  value: string;
};

/** Reads the editor's current content back out as markdown, one line per block. */
export const $exportTextareaMarkdown = () =>
  TextareaMarkdownUtil.unescapeControlCharacters(
    $convertToMarkdownString(TextareaMarkdownUtil.transformers, undefined, true)
  );

/**
 * Replaces the editor's entire content with the given markdown, read exactly as
 * written. This is the "content the user has" direction — what a paste or a test
 * writes — so it applies no legacy expansion.
 *
 * The root is always left holding at least one block, so there is always
 * something for a user (or a test) to click into. `shouldPreserveNewLines` is
 * what guarantees it: it emits one paragraph per line, and `''.split('\n')` is
 * `['']`.
 */
export const $replaceTextareaContent = ({ markdown }: { markdown: string }) => {
  $convertFromMarkdownString(
    markdown,
    TextareaMarkdownUtil.transformers,
    undefined,
    true
  );

  const root = $getRoot();

  if (root.isEmpty()) {
    root.append($createParagraphNode());
  }
};

/**
 * Loads a stored value. A legacy value spells a paragraph break as a single
 * `\n`, so it is expanded first and renders with the same empty block between
 * the lines that the previous editor produced. The persistence formatter
 * collapses it again, leaving the stored shape unchanged.
 */
export const $importTextareaMarkdown = ({
  markdown,
  formatting = 'legacy',
}: {
  markdown: string;
  formatting?: TextareaFormattingType;
}) => {
  $replaceTextareaContent({
    markdown:
      formatting === 'legacy'
        ? new TextareaReplacementUtil(markdown)
            .replacesOneNewLineWithTwo()
            .getText()
        : markdown,
  });
};

/**
 * Bridges Lexical's internal editor state to the controlled `value` prop.
 *
 * Lexical owns its own state, so the two directions have to be reconciled
 * explicitly. The ref records both halves of the last agreement: `parentValue`
 * is what the parent holds, and `exportedBaseline` is what that same content
 * exports back as. They differ whenever a stored value is not already in the
 * editor's canonical form — a legacy value, a table with alignment markers —
 * and keeping both is what stops such a value being written back on load.
 */
export const RawTextareaValuePlugin = ({
  formatting,
  formatValue,
  onChange,
  value,
}: RawTextareaValuePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const syncedRef = useRef<{
    exportedBaseline: string | null;
    parentValue: string;
  }>({ exportedBaseline: null, parentValue: value });

  /**
   * Declared before the import effect so the baseline is taken first. `read`
   * force-commits the mount-time import, which is what makes the export
   * readable here rather than one microtask later, after the listener below
   * would have reported it as a change.
   */
  useEffect(() => {
    if (syncedRef.current.exportedBaseline === null) {
      syncedRef.current.exportedBaseline =
        editor.read(() => formatValue($exportTextareaMarkdown())) ?? '';
    }

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const formatted = formatValue($exportTextareaMarkdown());

        if ((formatted ?? '') === syncedRef.current.exportedBaseline) return;

        syncedRef.current = {
          exportedBaseline: formatted ?? '',
          parentValue: formatted ?? '',
        };
        void onChange?.(formatted);
      });
    });
  }, [editor, formatValue, onChange]);

  useEffect(() => {
    if (value === syncedRef.current.parentValue) return;

    editor.update(() => {
      $importTextareaMarkdown({ markdown: value, formatting });

      syncedRef.current = {
        exportedBaseline: formatValue($exportTextareaMarkdown()) ?? '',
        parentValue: value,
      };
    });
  }, [editor, formatting, formatValue, value]);

  return null;
};
